import React, { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuthContext } from '../../context/AuthContext';
import { positionOptions } from './utils/playerUtils';
import { Player } from '../../types/Player';
import ConfirmModal from '../ConfirmModal';

interface Props {
  player: Player | null;
  onSave: (player: Player) => void;
  onCancel: () => void;
}

const PlayerForm: React.FC<Props> = ({ player, onSave, onCancel }) => {
  const playerPlaceholder = '/images/playerPlaceholder.png';
  const { user } = useAuthContext();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [formData, setFormData] = useState<Player>({
    firstName: '',
    lastName: '',
    shirtNumber: '',
    leagueId: '',
    positions: [],
    photoUrl: '',
    dobMonth: '',
    dobYear: '',
    teamId: user?.teamId || '',
    ...(player || {}),
  });

  const [file, setFile] = useState<File | null>(null);
  const [shirtNumberError, setShirtNumberError] = useState('');

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const years = Array.from(
    { length: 50 },
    (_, i) => `${new Date().getFullYear() - i}`
  );

  useEffect(() => {
    if (player) {
      setFormData(prev => ({ ...prev, ...player }));
    }
  }, [player]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(
      e.target.selectedOptions,
      option => option.value
    );
    setFormData(prev => ({ ...prev, positions: selected }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShirtNumberError('');

    const snapshot = await getDocs(
      query(
        collection(db, 'players'),
        where('teamId', '==', user?.teamId || '')
      )
    );

    const otherPlayers = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as Player))
      .filter(p => p.id !== formData.id);

    const duplicate = otherPlayers.find(
      p => p.shirtNumber === formData.shirtNumber
    );

    if (duplicate) {
      setShirtNumberError(
        `Shirt number already taken by ${duplicate.firstName} ${duplicate.lastName}`
      );
      return;
    }

    const playerData = {
      ...formData,
      teamId: user?.teamId || '',
    };

    const teamId = user?.teamId || 'unknown-team';
    const playersRef = collection(db, 'teams', teamId, 'players');

    if (formData.id) {
      await setDoc(doc(playersRef, formData.id), playerData);
      onSave({ ...playerData, id: formData.id });
    } else {
      const docRef = await addDoc(playersRef, playerData);
      onSave({ ...playerData, id: docRef.id });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className='bg-gray-100 p-4 rounded shadow mb-4'
    >
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <label className='flex flex-col'>
          <span className='text-sm font-bold mb-1'>First name:</span>
          <input
            name='firstName'
            value={formData.firstName}
            onChange={handleChange}
            className='p-2 border'
            required
          />
        </label>

        <label className='flex flex-col'>
          <span className='text-sm font-bold mb-1'>Last name:</span>
          <input
            name='lastName'
            value={formData.lastName}
            onChange={handleChange}
            className='p-2 border'
            required
          />
        </label>

        <label className='flex flex-col'>
          <span className='text-sm font-bold mb-1'>Shirt number:</span>
          <input
            type='number'
            name='shirtNumber'
            value={formData.shirtNumber}
            onChange={e => {
              const val = e.target.value;
              if (val.length <= 2 && /^\d*$/.test(val)) {
                setFormData(prev => ({ ...prev, shirtNumber: val }));
              }
            }}
            className='p-2 border'
            required
          />

          {shirtNumberError && (
            <span className='text-red-500 text-sm mt-1'>
              {shirtNumberError}
            </span>
          )}
        </label>

        <label className='flex flex-col'>
          <span className='text-sm font-bold mb-1'>
            Date of birth (Month / Year):
          </span>
          <div className='flex gap-2'>
            <select
              name='dobMonth'
              value={formData.dobMonth}
              onChange={handleChange}
              className='p-2 border w-1/2'
              required
            >
              <option value=''>Month</option>
              {months.map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              name='dobYear'
              value={formData.dobYear}
              onChange={handleChange}
              className='p-2 border w-1/2'
              required
            >
              <option value=''>Year</option>
              {years.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </label>

        <label className='flex flex-col'>
          <span className='text-sm font-bold mb-1'>League ID:</span>
          <input
            name='leagueId'
            value={formData.leagueId}
            onChange={handleChange}
            className='p-2 border'
            required
          />
        </label>

        <label className='flex flex-col'>
          <span className='text-sm font-bold mb-1'>Position(s):</span>
          <select
            multiple
            value={formData.positions}
            onChange={handlePositionChange}
            className='p-2 border h-32'
          >
            {positionOptions.map(pos => (
              <option key={pos.value} value={pos.value}>
                {pos.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className='flex justify-end gap-2 mt-4'>
        <button
          type='button'
          onClick={onCancel}
          className='px-4 py-2 bg-gray-500 text-white rounded'
        >
          Cancel
        </button>
        <button
          type='submit'
          className='px-4 py-2 bg-green-600 text-white rounded'
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default PlayerForm;
